import {HexColor, HSLRecord, RGB, RGBRecord} from '../../@types/colors';
import {SpecificLengthTuple} from '../../@types/SpecificLengthTuple';
import {RGBToHex} from './RGBToHex';

export type Matrix = SpecificLengthTuple<number, 9>;

export default class ColorMutator {
    protected r!: number;
    protected g!: number;
    protected b!: number;

    constructor(rgb: RGB = [0, 0, 0]) {
        if (Array.isArray(rgb)) {
            this.set(...rgb);
        } else {
            this.set(rgb.R, rgb.G, rgb.B);
        }
    }

    toString(): string {
        return `rgb(${Math.round(this.r)}, ${Math.round(this.g)}, ${Math.round(this.b)})`;
    }

    set(r: number, g: number, b: number): void {
        this.r = this.clamp(r);
        this.g = this.clamp(g);
        this.b = this.clamp(b);
    }

    logShade(percent: number): void {
        const floatPercent = Math.abs(percent) > 1 ? percent / 100 : percent;

        const isNegative = percent < 0;
        const intercept = isNegative ? 0 : floatPercent * 255 ** 2;
        const slope = isNegative ? 1 + floatPercent : 1 - floatPercent;

        this.set(
            Math.round((slope * this.r ** 2 + intercept) ** 0.5),
            Math.round((slope * this.g ** 2 + intercept) ** 0.5),
            Math.round((slope * this.b ** 2 + intercept) ** 0.5),
        );
    }

    linearShade(percent: number): void {
        const floatPercent = Math.abs(percent) > 1 ? percent / 100 : percent;

        const isNegative = percent < 0;
        const intercept = isNegative ? 0 : 255 * floatPercent;
        const slope = isNegative ? 1 + floatPercent : 1 - floatPercent;

        this.set(
            Math.round(this.r * slope + intercept),
            Math.round(this.g * slope + intercept),
            Math.round(this.b * slope + intercept),
        );
    }

    lighten(percent: number, linear = true): void {
        this[linear ? 'linearShade' : 'logShade'](percent);
    }

    darken(percent: number, linear = true): void {
        this[linear ? 'linearShade' : 'logShade'](-percent);
    }

    hueRotate(angle = 0): void {
        angle = angle / 180 * Math.PI;

        const sin = Math.sin(angle);
        const cos = Math.cos(angle);

        this.multiply([
            0.213 + cos * 0.787 - sin * 0.213,
            0.715 - cos * 0.715 - sin * 0.715,
            0.072 - cos * 0.072 + sin * 0.928,
            0.213 - cos * 0.213 + sin * 0.143,
            0.715 + cos * 0.285 + sin * 0.140,
            0.072 - cos * 0.072 - sin * 0.283,
            0.213 - cos * 0.213 - sin * 0.787,
            0.715 - cos * 0.715 + sin * 0.715,
            0.072 + cos * 0.928 + sin * 0.072,
        ]);
    }

    grayscale(value = 1): void {
        this.multiply([
            0.2126 + 0.7874 * (1 - value),
            0.7152 - 0.7152 * (1 - value),
            0.0722 - 0.0722 * (1 - value),
            0.2126 - 0.2126 * (1 - value),
            0.7152 + 0.2848 * (1 - value),
            0.0722 - 0.0722 * (1 - value),
            0.2126 - 0.2126 * (1 - value),
            0.7152 - 0.7152 * (1 - value),
            0.0722 + 0.9278 * (1 - value),
        ]);
    }

    sepia(value = 1): void {
        this.multiply([
            0.393 + 0.607 * (1 - value),
            0.769 - 0.769 * (1 - value),
            0.189 - 0.189 * (1 - value),
            0.349 - 0.349 * (1 - value),
            0.686 + 0.314 * (1 - value),
            0.168 - 0.168 * (1 - value),
            0.272 - 0.272 * (1 - value),
            0.534 - 0.534 * (1 - value),
            0.131 + 0.869 * (1 - value),
        ]);
    }

    saturate(value = 1): void {
        this.multiply([
            0.213 + 0.787 * value,
            0.715 - 0.715 * value,
            0.072 - 0.072 * value,
            0.213 - 0.213 * value,
            0.715 + 0.285 * value,
            0.072 - 0.072 * value,
            0.213 - 0.213 * value,
            0.715 - 0.715 * value,
            0.072 + 0.928 * value,
        ]);
    }

    multiply(matrix: Matrix): void {
        this.set(
            this.r * matrix[0] + this.g * matrix[1] + this.b * matrix[2],
            this.r * matrix[3] + this.g * matrix[4] + this.b * matrix[5],
            this.r * matrix[6] + this.g * matrix[7] + this.b * matrix[8],
        );
    }

    brightness(value = 1): void {
        this.linear(value);
    }

    contrast(value = 1): void {
        this.linear(value, -(0.5 * value) + 0.5);
    }

    linear(slope = 1, intercept = 0): void {
        this.set(
            this.r * slope + intercept * 255,
            this.g * slope + intercept * 255,
            this.b * slope + intercept * 255,
        );
    }

    invert(value = 1): void {
        this.set(
            (value + this.r / 255 * (1 - 2 * value)) * 255,
            (value + this.g / 255 * (1 - 2 * value)) * 255,
            (value + this.b / 255 * (1 - 2 * value)) * 255,
        );
    }

    clamp(value: number): number {
        if (value > 255) {
            value = 255;
        } else if (value < 0) {
            value = 0;
        }

        return value;
    }

    hsl(): HSLRecord {
        const r = this.r / 255;
        const g = this.g / 255;
        const b = this.b / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);

        let h = 0, s = 0;
        const l: number = (max + min) / 2;

        if (max !== min) {
            const d = max - min;

            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;

                case g:
                    h = (b - r) / d + 2;
                    break;

                case b:
                    h = (r - g) / d + 4;
                    break;

                default:
                    h = 0;
                    break;
            }

            h /= 6;
        }

        return {
            H: h * 100,
            S: s * 100,
            L: l * 100,
        };
    }

    rgb(): RGBRecord {
        return {
            R: this.r,
            G: this.g,
            B: this.b,
        };
    }

    hex(): HexColor {
        return RGBToHex(this.rgb());
    }
}
